// eslint-disable-next-line @nx/enforce-module-boundaries
import { apiSSO } from '@rfacil/shared/services';
import axios from 'axios';

/**
 * @interface UsuarioDetalleDTO
 * @description Representa la información detallada de un usuario en el sistema (datos personales, fiscales, etc.).
 * @property {number} Id - Identificador único del detalle de usuario.
 * @property {number} IdUsuario - Identificador del usuario al que pertenece este detalle.
 * @property {string} Nombre - Nombre del usuario (requerido).
 * @property {string} Apaterno - Apellido paterno del usuario (requerido).
 * @property {string} [Amaterno] - Apellido materno del usuario (opcional).
 * @property {string} [Rfc] - Registro Federal de Contribuyentes del usuario (opcional).
 * @property {string} [NumeroEmpleado] - Número de empleado del usuario (opcional).
 * @property {string} [NumeroProveedor] - Número de proveedor del usuario (opcional).
 * @property {string} [IdentificadorFiscal] - Identificador fiscal del usuario (opcional).
 * @property {boolean} [EsPPD] - Indica si el usuario es Persona Políticamente Expuesta (opcional).
 * @property {number} [Unit] - Unidad organizacional del usuario (opcional).
 * @property {string} [NombreCompleto] - Nombre completo del usuario (calculado, opcional).
 * @property {string} UserName - Nombre de usuario asociado.
 */
export interface UsuarioDetalleDTO {
  Id: number;
  IdUsuario: number;
  Nombre: string;
  Apaterno: string;
  Amaterno?: string;
  Rfc?: string | null;
  NumeroEmpleado?: string;
  NumeroProveedor?: string;
  IdentificadorFiscal?: string;
  EsPPD?: boolean;
  Unit?: number;
  NombreCompleto?: string;
  UserName: string;
}

/**
 * @interface CrearUsuarioDetalleDTO
 * @description DTO utilizado para crear la información detallada de un usuario.
 * @property {number} IdUsuario - Identificador del usuario al que pertenecerá este detalle (requerido).
 * @property {string} Nombre - Nombre del usuario (requerido).
 * @property {string} Apaterno - Apellido paterno del usuario (requerido).
 * @property {string} [Amaterno] - Apellido materno del usuario (opcional).
 * @property {string} [Rfc] - Registro Federal de Contribuyentes del usuario (opcional).
 * @property {string} [NumeroEmpleado] - Número de empleado del usuario (opcional).
 * @property {string} [NumeroProveedor] - Número de proveedor del usuario (opcional).
 * @property {string} [IdentificadorFiscal] - Identificador fiscal del usuario (opcional).
 * @property {boolean} [EsPPD] - Indica si el usuario es Persona Políticamente Expuesta (opcional).
 * @property {number} [Unit] - Unidad organizacional del usuario (opcional).
 */
export interface CrearUsuarioDetalleDTO {
  IdUsuario: number;
  Nombre: string;
  Apaterno: string;
  Amaterno?: string;
  Rfc?: string | null;
  NumeroEmpleado?: string;
  NumeroProveedor?: string;
  IdentificadorFiscal?: string;
  EsPPD?: boolean;
  Unit?: number;
}

/**
 * @interface EditarUsuarioDetalleDTO
 * @description DTO utilizado para editar la información detallada de un usuario existente.
 * @property {number} IdUsuario - Identificador del usuario cuyo detalle se editará (requerido).
 * @property {string} Nombre - Nuevo nombre del usuario (requerido).
 * @property {string} Apaterno - Nuevo apellido paterno del usuario (requerido).
 * @property {string} [Amaterno] - Nuevo apellido materno del usuario (opcional).
 * @property {string} [Rfc] - Nuevo Registro Federal de Contribuyentes del usuario (opcional).
 * @property {string} [NumeroEmpleado] - Nuevo número de empleado del usuario (opcional).
 * @property {string} [NumeroProveedor] - Nuevo número de proveedor del usuario (opcional).
 * @property {string} [IdentificadorFiscal] - Nuevo identificador fiscal del usuario (opcional).
 * @property {boolean} [EsPPD] - Nuevo indicador de Persona Políticamente Expuesta (opcional).
 * @property {number} [Unit] - Nueva unidad organizacional del usuario (opcional).
 */
export interface EditarUsuarioDetalleDTO {
  Id: number;
  IdUsuario: number;
  Nombre: string;
  Apaterno: string;
  Amaterno?: string;
  Rfc?: string | null;
  NumeroEmpleado?: string;
  NumeroProveedor?: string;
  IdentificadorFiscal?: string;
  EsPPD?: boolean;
  Unit?: number;
}

/**
 * @function getUsuarioDetalle
 * @description Obtiene el detalle de un usuario por su IdUsuario.
 * @param {number} idUsuario - Identificador del usuario del cual se quiere obtener el detalle.
 * @returns {Promise<UsuarioDetalleDTO | null>} Una promesa que se resuelve con el detalle del usuario o null si no existe.
 * @throws {Error} Si ocurre un error al obtener el detalle del usuario desde la API.
 */
export async function getUsersDet(
  idUsuario: number
): Promise<UsuarioDetalleDTO | null> {
  try {
    const response = await apiSSO.get(`/Usuario/ObtenerUsuarioDetallexIDUsuario/${idUsuario}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // Si el error es 404, significa que no existe detalle para este usuario
      if (error.response?.status === 404) {
        return null;
      }
      console.error(`Error de API en getUsuarioDetalle: ${error.response?.status} - ${error.response?.data}`, error);
      throw new Error(error.response?.data);
    } else {
      console.error('Error al obtener el detalle del usuario:', error);
      throw new Error('Error inesperado al obtener el detalle del usuario.');
    }
  }
}

/**
 * @function editUserDet
 * @description Edita la información detallada de un usuario existente en el sistema.
 * @param {EditarUsuarioDetalleDTO} data - Datos del detalle de usuario a editar (IdUsuario, Nombre, Apaterno, y otros campos opcionales).
 * @returns {Promise<UsuarioDetalleDTO>} Una promesa que se resuelve con el detalle de usuario actualizado.
 * @throws {Error} Si ocurre un error al editar el detalle de usuario en la API (por ejemplo, usuario no encontrado, datos inválidos, etc.).
 */
export async function editUserDet(
  data: EditarUsuarioDetalleDTO
): Promise<UsuarioDetalleDTO> {
  try {
    const response = await apiSSO.put('/Usuario/EditarUsuarioDetalle', data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(`Error de API en editUserDetail: ${error.response?.status} - ${error.response?.data}`, error);
      throw new Error(error.response?.data);
    } else {
      console.error('Error al editar el usuario detalle:', error);
      throw new Error('Error inesperado al editar el usuario detalle.');
    }
  }
}

/**
 * @function createUsuarioDetalle
 * @description Crea la información detallada de un usuario existente.
 * @param {CrearUsuarioDetalleDTO} data - Datos del detalle de usuario a crear.
 * @returns {Promise<UsuarioDetalleDTO>} Una promesa que se resuelve con el detalle de usuario creado.
 * @throws {Error} Si ocurre un error al crear el detalle de usuario en la API.
 */
export async function createUserDet(
  data: CrearUsuarioDetalleDTO
): Promise<UsuarioDetalleDTO> {
  try {
    const response = await apiSSO.post('/Usuario/CrearUsuarioDetalle', data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(`Error de API en createUsuarioDetalle: ${error.response?.status} - ${error.response?.data}`, error);
      throw new Error(error.response?.data);
    } else {
      console.error('Error al crear el usuario detalle:', error);
      throw new Error('Error inesperado al crear el usuario detalle.');
    }
  }
}
